/**
 * CR043: READ Summary Stats Component
 */

import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle } from "lucide-react";
import { READ_FAILURE_REASONS, type ReadFailureReason, type PocRunReadSummary } from "@/lib/read-phase/types";

interface ReadSummaryStatsProps {
  summary: PocRunReadSummary;
  totalMerchants: number;
}

export function ReadSummaryStats({ summary, totalMerchants }: ReadSummaryStatsProps) {
  const blockedPercent = totalMerchants > 0 ? (summary.merchants_blocked / totalMerchants) * 100 : 0;
  const showWarningBanner = blockedPercent >= 20;
  
  // Ensure top_failure_reasons is always an array
  const failureReasons = Array.isArray(summary.top_failure_reasons) 
    ? summary.top_failure_reasons 
    : [];

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">READ Attempts (avg)</p>
            <p className="text-2xl font-bold">
              {summary.read_attempts_avg?.toFixed(1) || '-'}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Merchants Readable</p>
            <p className="text-2xl font-bold text-green-600">
              {summary.merchants_readable}/{totalMerchants}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Merchants Blocked</p>
            <p className="text-2xl font-bold text-red-600">
              {summary.merchants_blocked}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Top Failure Reasons</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {failureReasons.length > 0 ? (
                failureReasons.slice(0, 2).map((item, idx) => {
                  const tooltip = READ_FAILURE_REASONS[item.reason as ReadFailureReason] || item.reason;
                  return (
                    <Tooltip key={idx}>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="text-xs">
                          {item.reason.replace('FAILED_', '').slice(0, 12)} ({item.count})
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })
              ) : (
                <span className="text-sm text-muted-foreground">None</span>
              )}
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        {showWarningBanner && (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Many merchants could not be read
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Most common reasons: {failureReasons.slice(0, 2).map(r => 
                  r.reason.replace('FAILED_', '').replace(/_/g, ' ')
                ).join(', ')}. 
                Check cookie/location handling and retry settings.
              </p>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
